import { MenuItem, Stack, Typography } from '@mui/material';
import { isEmpty } from 'lodash';

import { ButtonWithPopover } from '@/components/form/baseInputs/Button/ButtonWithPopover';
import { CubeIcon } from '@/components/icons/CubeIcon';
import { ShieldTickIcon } from '@/components/icons/ShieldTickIcon';
import { useReportError } from '@/hooks/useReportError';
import { COLORS } from '@/styles/tokens/colors';

import { useHouseholdChatContext } from '../contexts/householdChat.context';
import { useHouseholdChatPersonasQuery } from '../graphql/HouseholdChat.generated';
import { HouseholdChatPersonaHoverCard } from './HouseholdChatPersonaHoverCard';

/**
 * The "Workflow" control lets the user run their next messages through a
 * specialized persona (a purpose-built specialist configuration on the
 * backend) instead of the default chat behavior. Shown next to the
 * answer-depth mode selector when the household_chat_specialized_persona_poc
 * flag is on.
 *
 * The menu lists each server-supported persona with its display name and
 * subtitle, and hovering a row opens a card with the workflow details. There
 * is no "default" row. The selected persona is cleared through its chip's
 * remove button in the composer.
 *
 * The selection is sourced from (and written back to) the chat context, so it
 * applies to both typed messages and suggestion chips. It is not persisted
 * server-side: selecting or starting another conversation resets it.
 */
export function HouseholdChatPersonaSelector() {
  const { personaSelectorEnabled, setChatPersona } = useHouseholdChatContext();
  const { reportError } = useReportError();

  const { data } = useHouseholdChatPersonasQuery({
    // The composer only renders this selector when the persona POC flag is
    // on, but guard here too so a stray mount can't fire the query.
    skip: !personaSelectorEnabled,
    onError: (queryError) => {
      // Persona turns simply aren't offered when the catalog can't be
      // loaded; the button hides below.
      reportError('Failed to load household chat personas', queryError);
    },
  });

  const personaOptions = data?.householdChatPersonas ?? [];

  // Nothing to offer (catalog still loading, failed, or empty): hide the
  // button rather than render a disabled dead-end.
  if (isEmpty(personaOptions)) {
    return null;
  }

  return (
    <ButtonWithPopover
      aria-label="Workflow"
      // Match the compact, neutral mode selector next to it.
      variant="transparent"
      size="xs"
      buttonSx={{
        maxHeight: '20px',
        py: 0,
        bgcolor: COLORS.GRAY[50],
        borderRadius: '4px',
      }}
      startIcon={CubeIcon}
      iconProps={{ 'aria-hidden': true, size: 16, color: COLORS.BRAND.ORANGE }}
      label={<Typography variant="body-xs">Workflow</Typography>}
      popperVariant="menuAbove"
      popperProps={{
        anchorOrigin: { vertical: -8, horizontal: 'left' },
        transformOrigin: { vertical: 'bottom', horizontal: 'left' },
        slotProps: {
          paper: {
            sx: {
              width: 259,
              mt: 0,
            },
          },
        },
      }}
    >
      {personaOptions.map((option) => {
        return (
          <HouseholdChatPersonaHoverCard
            key={option.persona}
            personaOption={option}
          >
            <MenuItem onClick={() => setChatPersona(option.persona)}>
              <Stack
                direction="row"
                spacing={1}
                alignItems="flex-start"
                // The fixed content width reproduces the 251px row with 8px
                // horizontal padding inside the 259px Figma menu.
                sx={{ width: 235, whiteSpace: 'normal' }}
              >
                <ShieldTickIcon
                  aria-hidden
                  size={16}
                  color={COLORS.TEXT.PRIMARY}
                />
                <Stack spacing={0.25} sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Typography
                    variant="body-xs"
                    noWrap
                    sx={{ color: COLORS.TEXT.PRIMARY }}
                  >
                    {option.displayName}
                  </Typography>
                  <Typography variant="body-xs">{option.subtitle}</Typography>
                </Stack>
              </Stack>
            </MenuItem>
          </HouseholdChatPersonaHoverCard>
        );
      })}
    </ButtonWithPopover>
  );
}
